import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { StringHelper } from 'tiime-expert-utils';

import { AntiMoneyLaunderingForm } from '@common/forms/anti-money-laundering-form';
import { MissionAcceptanceFileType } from '@enums/mission-acceptance-file-type.enum';
import { MissionAcceptanceStatus } from '@enums/mission-acceptance-status.enum';
import { MissionAcceptanceType } from '@enums/mission-acceptance-type.enum';
import { TaxRegimeCode } from '@enums/tax-regime-code.enum';
import { VatSystemCode } from '@enums/vat-system-code.enum';
import { MissionAcceptanceFileForm } from '@forms/mission-acceptance-file-form';
import { Contract } from '@gi/models/contract';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { LegalStructure } from '@models/legal-structure';
import { MissionAcceptance } from '@models/mission-acceptance/mission-acceptance';
import { MissionAcceptanceFile } from '@models/mission-acceptance-file';
import { Signatory } from '@models/signatory';
import { User } from '@models/user';

import { MissionAcceptanceAcceptationForm } from './mission-acceptance-acceptation-form';
import { MissionAcceptanceInformationsForm } from './mission-acceptance-informations-form';

export type MissionAcceptanceFormType = {
  id: FormControl<number | undefined>;
  missionAcceptanceStatus: FormControl<MissionAcceptanceStatus | null>;
  statusAt: FormControl<Date | null>;
  statusBy: FormControl<BusinessUser | null>;
  legalStructure: FormControl<LegalStructure | null>;
  signatory: FormControl<Signatory | null>;
  company: FormControl<Company | null>;
  companyName: FormControl<string | null>;
  companyReference: FormControl<string | null>;
  companyLegalForm: FormControl<string | null>;
  companyApeCode: FormControl<string | null>;
  companyTaxRegime: FormControl<TaxRegimeCode | null>;
  companyVatSystem: FormControl<VatSystemCode | null>;
  companyStreet: FormControl<string | null>;
  companyPostalCode: FormControl<string | null>;
  companyCity: FormControl<string | null>;
  contact: FormControl<User | null>;
  contactLastName: FormControl<string | null>;
  contactFirstName: FormControl<string | null>;
  contactEmail: FormControl<string | null>;
  contactPhone: FormControl<string | null>;
  contactStreet: FormControl<string | null>;
  contactPostalCode: FormControl<string | null>;
  contactCity: FormControl<string | null>;
  informations: MissionAcceptanceInformationsForm;
  acceptation: MissionAcceptanceAcceptationForm;
  antiMoneyLaundering: AntiMoneyLaunderingForm;
  files: FormArray<MissionAcceptanceFileForm>;
  filesToDelete: FormArray<MissionAcceptanceFileForm>;
};

export class MissionAcceptanceForm extends FormGroup<MissionAcceptanceFormType> {
  readonly requiredToValidateControls: FormControl[] = [
    // informations
    this.controls.informations.controls.type,

    // acceptation
    this.controls.acceptation.controls.accountingOrganizationRisk,
    this.controls.acceptation.controls.activityRisk,
    this.controls.acceptation.controls.clientAdditionalNeeds,
    this.controls.acceptation.controls.clientContacted,
    this.controls.acceptation.controls.companyConsistentWithPartnerMethods,
    this.controls.acceptation.controls.companyInvestigation,
    this.controls.acceptation.controls.contractConsistentWithProposedMission,
    this.controls.acceptation.controls.fiscalSocialClientHistoryRisk,
    this.controls.acceptation.controls.independentFromClient,
    this.controls.acceptation.controls.legalCharacteristicsRisk,
    this.controls.acceptation.controls.legalStructureCompetentForMission,
    this.controls.acceptation.controls.missionObject,
    this.controls.acceptation.controls.specificDeadlines,

    // antiMoneyLaundering
    this.controls.antiMoneyLaundering.controls.entityIdentificationDocumentation,
    this.controls.antiMoneyLaundering.controls.highRiskActivitySector,
    this.controls.antiMoneyLaundering.controls.frequentLegalChanges,
    this.controls.antiMoneyLaundering.controls.effectiveBeneficiaryIdentified,
    this.controls.antiMoneyLaundering.controls.effectiveBeneficiaryIdentityPiece,
    this.controls.antiMoneyLaundering.controls.directorsPoliticallyExposed,
    this.controls.antiMoneyLaundering.controls.directorsIntegrityDoubts,
    this.controls.antiMoneyLaundering.controls.periodOperationsConsistent,
    this.controls.antiMoneyLaundering.controls.complexOperations,
    this.controls.antiMoneyLaundering.controls.irregularHighAmounts,
    this.controls.antiMoneyLaundering.controls.operationsWithoutEconomicProofs,
    this.controls.antiMoneyLaundering.controls.fundsOriginsJustified,
    this.controls.antiMoneyLaundering.controls.riskedTransactions,
    this.controls.antiMoneyLaundering.controls.representantIdentificationValid
  ];

  readonly requiredToValidateControlsWhenPreviousAccountantRecovery = [
    this.controls.informations.controls.previousAccountantContacted,
    this.controls.informations.controls.previousAccountantLetterDate,
    this.controls.informations.controls.previousAccountantOppositionExisted
  ];

  readonly requiredToValidateControlsWhenCreation = [
    this.controls.informations.controls.directorsOwnersStrangerCompanies,
    this.controls.informations.controls.directorsFrenchResidents,
    this.controls.informations.controls.directorsOwnersLiquidationCompanies,
    this.controls.informations.controls.directorsOwnersOtherAccountantCompanies
  ];

  get contactErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.controls.contact, 'Le contact est requis');
  }

  get signatoryErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.controls.signatory, 'Le signataire est requis');
  }

  get identityPieceIsNeededAndMissing(): boolean {
    const identityPieceIsNeeded = [
      MissionAcceptanceType.previousAccountantRecovery,
      MissionAcceptanceType.recoveryWithoutAccountant
    ].includes(this.controls.informations.controls.type.value);
    const identityPieceIsMissing = !this.controls.files.controls?.some(
      (form: MissionAcceptanceFileForm) => form.type.value === MissionAcceptanceFileType.identityPiece
    );

    return identityPieceIsNeeded && identityPieceIsMissing;
  }

  constructor() {
    super({
      id: new FormControl<number | undefined>(undefined),
      missionAcceptanceStatus: new FormControl<MissionAcceptanceStatus | null>(null),
      statusAt: new FormControl<Date | null>(null),
      statusBy: new FormControl<BusinessUser | null>(null),
      legalStructure: new FormControl<LegalStructure | null>(null),
      signatory: new FormControl<Signatory | null>(null, Validators.required),
      company: new FormControl<Company | null>(null),
      companyName: new FormControl<string | null>(null),
      companyReference: new FormControl<string | null>(null),
      companyLegalForm: new FormControl<string | null>(null),
      companyApeCode: new FormControl<string | null>(null),
      companyTaxRegime: new FormControl<TaxRegimeCode | null>(null),
      companyVatSystem: new FormControl<VatSystemCode | null>(null),
      companyStreet: new FormControl<string | null>(null),
      companyPostalCode: new FormControl<string | null>(null),
      companyCity: new FormControl<string | null>(null),
      contact: new FormControl<User | null>(null, Validators.required),
      contactLastName: new FormControl<string | null>(null),
      contactFirstName: new FormControl<string | null>(null),
      contactEmail: new FormControl<string | null>(null),
      contactPhone: new FormControl<string | null>(null),
      contactStreet: new FormControl<string | null>(null),
      contactPostalCode: new FormControl<string | null>(null),
      contactCity: new FormControl<string | null>(null),
      informations: new MissionAcceptanceInformationsForm(),
      acceptation: new MissionAcceptanceAcceptationForm(),
      antiMoneyLaundering: new AntiMoneyLaunderingForm(),
      files: new FormArray<MissionAcceptanceFileForm>([]),
      filesToDelete: new FormArray<MissionAcceptanceFileForm>([])
    });
  }

  fromMissionAcceptance(missionAcceptance: MissionAcceptance): void {
    this.patchValue({
      id: missionAcceptance.id,
      missionAcceptanceStatus: missionAcceptance.status,
      statusAt: missionAcceptance.statusAt,
      statusBy: missionAcceptance.statusBy,
      legalStructure: missionAcceptance.legalStructure,
      signatory: missionAcceptance.signatory,
      company: missionAcceptance.company,
      companyName: missionAcceptance.companyName,
      companyReference: missionAcceptance.companyReference,
      companyLegalForm: missionAcceptance.companyLegalForm,
      companyApeCode: missionAcceptance.companyApeCode,
      companyTaxRegime: missionAcceptance.companyTaxRegime,
      companyVatSystem: missionAcceptance.companyVatSystem,
      companyStreet: missionAcceptance.companyStreet,
      companyPostalCode: missionAcceptance.companyPostalCode,
      companyCity: missionAcceptance.companyCity,
      contact: missionAcceptance.contact,
      contactLastName: missionAcceptance.contactLastName,
      contactFirstName: missionAcceptance.contactFirstName,
      contactEmail: missionAcceptance.contactEmail,
      contactPhone: missionAcceptance.contactPhone,
      contactStreet: missionAcceptance.contactStreet,
      contactPostalCode: missionAcceptance.contactPostalCode,
      contactCity: missionAcceptance.contactCity
    });

    this.controls.files.clear();
    this.controls.filesToDelete.clear();

    (missionAcceptance.missionAcceptanceFiles ?? []).forEach((file: MissionAcceptanceFile) => this.addFile(file));

    this.controls.informations.fromMissionAcceptanceInformations(missionAcceptance.informations);

    this.controls.acceptation.fromMissionAcceptanceAcceptation(missionAcceptance.acceptation);

    this.controls.antiMoneyLaundering.fromAntiMoneyLaundering(missionAcceptance.antiMoneyLaundering);
  }

  toMissionAcceptance(): MissionAcceptance {
    const missionAcceptance = new MissionAcceptance();

    missionAcceptance.id = this.controls.id.value;
    missionAcceptance.status = this.controls.missionAcceptanceStatus.value ?? null;
    missionAcceptance.statusAt = this.controls.statusAt.value ?? null;
    missionAcceptance.statusBy = this.controls.statusBy.value ?? null;
    missionAcceptance.legalStructure = this.controls.legalStructure.value ?? null;
    missionAcceptance.signatory = this.controls.signatory.value ?? null;
    missionAcceptance.company = this.controls.company.value ?? null;
    missionAcceptance.companyName = this.controls.companyName.value ?? null;
    missionAcceptance.companyReference = this.controls.companyReference.value ?? null;
    missionAcceptance.companyLegalForm = this.controls.companyLegalForm.value ?? null;
    missionAcceptance.companyApeCode = this.controls.companyApeCode.value ?? null;
    missionAcceptance.companyTaxRegime = this.controls.companyTaxRegime.value ?? null;
    missionAcceptance.companyVatSystem = this.controls.companyVatSystem.value ?? null;
    missionAcceptance.companyStreet = this.controls.companyStreet.value ?? null;
    missionAcceptance.companyPostalCode = this.controls.companyPostalCode.value ?? null;
    missionAcceptance.companyCity = this.controls.companyCity.value ?? null;
    missionAcceptance.contact = this.controls.contact.value ?? null;
    missionAcceptance.contactLastName = this.controls.contactLastName.value ?? null;
    missionAcceptance.contactFirstName = this.controls.contactFirstName.value ?? null;
    missionAcceptance.contactEmail = this.controls.contactEmail.value ?? null;
    missionAcceptance.contactPhone = this.controls.contactPhone.value ?? null;
    missionAcceptance.contactStreet = this.controls.contactStreet.value ?? null;
    missionAcceptance.contactPostalCode = this.controls.contactPostalCode.value ?? null;
    missionAcceptance.contactCity = this.controls.contactCity.value ?? null;
    missionAcceptance.informations = this.controls.informations.toMissionAcceptanceInformations();
    missionAcceptance.acceptation = this.controls.acceptation.toMissionAcceptanceAcceptation();
    missionAcceptance.antiMoneyLaundering = this.controls.antiMoneyLaundering.toAntiMoneyLaundering();

    return missionAcceptance;
  }

  setCompanyFields(company: Company): void {
    this.patchValue({
      companyName: company?.name,
      companyReference: company?.reference,
      companyLegalForm: company?.legalForm,
      companyApeCode: company?.apeCode?.code,
      companyTaxRegime: company?.taxRegime,
      companyVatSystem: company?.vatSystem?.code,
      companyStreet: company?.street,
      companyPostalCode: company?.postalCode,
      companyCity: company?.city
    });
  }

  setContactFields(contact: User): void {
    this.patchValue({
      contactLastName: contact?.lastName,
      contactFirstName: contact?.firstName,
      contactEmail: contact?.email,
      contactPhone: contact?.phone,
      contactStreet: contact?.mailingStreet,
      contactPostalCode: contact?.mailingPostalCode,
      contactCity: contact?.mailingCity
    });
  }

  addToValidateControlsRequired(): void {
    const missionAcceptanceType = this.controls.informations.controls.type.value;
    let requiredControls = [...this.requiredToValidateControls];

    switch (missionAcceptanceType) {
      case MissionAcceptanceType.creation:
        requiredControls = [...requiredControls, ...this.requiredToValidateControlsWhenCreation];
        break;
      case MissionAcceptanceType.previousAccountantRecovery:
        requiredControls = [...requiredControls, ...this.requiredToValidateControlsWhenPreviousAccountantRecovery];
        break;
    }

    requiredControls.forEach((control: AbstractControl) => {
      control.addValidators(Validators.required);
      control.updateValueAndValidity();
    });

    if (this.identityPieceIsNeededAndMissing) {
      this.controls.informations.addMissingIdentityPieceError();
    }
  }

  removeToValidateControlsRequired(): void {
    [
      ...this.requiredToValidateControls,
      ...this.requiredToValidateControlsWhenPreviousAccountantRecovery,
      ...this.requiredToValidateControlsWhenCreation
    ].forEach((control: AbstractControl) => {
      control.removeValidators(Validators.required);
      control.updateValueAndValidity();
    });

    this.controls.informations.removeMissingIdentityPieceError();
  }

  addFile(file: MissionAcceptanceFile): void {
    const fileForm: MissionAcceptanceFileForm = new MissionAcceptanceFileForm();
    fileForm.fromFile(file);
    this.controls.files.push(fileForm);
  }

  removeFile(uniqId: string): void {
    const index = this.controls.files.controls.findIndex(
      (fileForm: MissionAcceptanceFileForm) => fileForm.uniqId === uniqId
    );
    if (index !== -1) {
      const fileForm: MissionAcceptanceFileForm = this.controls.files.controls.find(
        (fileForm: MissionAcceptanceFileForm) => fileForm.uniqId === uniqId
      );
      if (fileForm?.id.value) {
        this.addFileToDelete(fileForm);
      }

      this.controls.files.removeAt(index);
    }
  }

  addFileToDelete(fileForm: MissionAcceptanceFileForm): void {
    this.controls.filesToDelete.push(fileForm);
  }

  addFileToUpload(file: File, type = MissionAcceptanceFileType.other): void {
    const fileForm: MissionAcceptanceFileForm = new MissionAcceptanceFileForm();
    fileForm.name.setValue(file.name);
    fileForm.type.setValue(type);
    fileForm.content.setValue(file);

    this.controls.files.push(fileForm);
  }

  addContractFileToUpload(contract: Contract): void {
    const fileForm: MissionAcceptanceFileForm = new MissionAcceptanceFileForm();
    const companyName = contract.company?.name ?? null;
    const signedAt = contract.signedAt ?? null;
    fileForm.name.setValue(
      `contrat${companyName ? `_${StringHelper.normalizeToLowerCase(companyName).replace(/\s/g, '_')}` : ''}${signedAt ? `_${DateHelper.format(signedAt, 'DD_MM_YYYY')}` : ''}.pdf`
    );
    fileForm.title.setValue(
      `Contrat${companyName ? ` ${companyName}` : ''}${signedAt ? ` ${DateHelper.format(contract.signedAt, 'DD-MM-YYYY')}` : ''}`
    );
    fileForm.type.setValue(MissionAcceptanceFileType.contractLdm);
    fileForm.contractId.setValue(contract.id);

    this.controls.files.push(fileForm);
  }

  getFilesToUpload(): MissionAcceptanceFile[] {
    return this.controls.files.controls
      .filter((fileForm: MissionAcceptanceFileForm) => !fileForm.id.value && fileForm.content.value)
      .map((fileForm: MissionAcceptanceFileForm) => fileForm.toFile());
  }

  getContractsFilesToUpload(): MissionAcceptanceFile[] {
    return this.controls.files.controls
      .filter((fileForm: MissionAcceptanceFileForm) => !fileForm.id.value && fileForm.contractId.value)
      .map((fileForm: MissionAcceptanceFileForm) => fileForm.toFile());
  }

  getFilesToDelete(): number[] {
    return this.controls.filesToDelete.controls.map((fileForm: MissionAcceptanceFileForm) => fileForm.id.value);
  }
}
